import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Users, Trash2, Plus, ClipboardPaste } from "lucide-react";
import { ImportList } from "@/components/hyundai/AdvisorRoster";

// Rows like "| Angel Adame | 121 | 335 |" or "Angel Adame<tab>121<tab>335" -> name, DMS ID, Hyundai tech #.
export const parseTechs = (text) => text.split(/\n/).map((l) => l.trim().replace(/^\||\|$/g, "").split(/\s*\|\s*|\t+/).map((x) => x.trim()))
  .map((c) => (c.length >= 3 ? c : (c[0].match(/^(.+?)\s+(\d+)\s+(\d+)$/) || []).slice(1)))
  .filter((c) => c.length >= 3 && /[a-z]/i.test(c[0]) && /^\d+$/.test(c[1]) && /^\d+$/.test(c[2]))
  .map(([name, dms_id, number]) => ({ name, dms_id, number }));

function PasteList({ base, store, onDone, numLabel }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const rows = parseTechs(text);
  const run = async () => {
    setBusy(true);
    let added = 0, skipped = 0;
    for (const r of rows) {
      try { await api.post(`${base}/technicians`, { ...r, dealership_id: store }); added++; } catch { skipped++; }
    }
    setBusy(false);
    setText("");
    toast.success(`Added ${added} technician${added === 1 ? "" : "s"}${skipped ? ` - ${skipped} already saved` : ""}`);
    onDone();
  };
  return (
    <div className="space-y-2">
      <Textarea rows={3} placeholder={`Paste a list: Name | DMS ID | ${numLabel}`} value={text} onChange={(e) => setText(e.target.value)} data-testid="hy-tech-paste" />
      <Button variant="outline" size="sm" className="gap-1" disabled={busy || !rows.length || !store} onClick={run} data-testid="hy-tech-paste-add">
        <ClipboardPaste className="h-3.5 w-3.5" /> {busy ? "Adding..." : `Add ${rows.length} from list`}
      </Button>
    </div>
  );
}

export function TechRoster({ techs, onChange, base = "/hyundai", store, storeName }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", dms_id: "", number: "" });
  const hy = base === "/hyundai";
  const numLabel = hy ? "Hyundai #" : "GMIN";
  const shown = techs.filter((t) => !t.dealership_id || t.dealership_id === store);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const add = async () => {
    try {
      await api.post(`${base}/technicians`, { ...f, dealership_id: store });
      setF({ name: "", dms_id: "", number: "" });
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add technician");
    }
  };
  const remove = async (id) => { await api.delete(`${base}/technicians/${id}`); onChange(); };
  const moveHere = async (id) => { await api.patch(`${base}/technicians/${id}`, { dealership_id: store }); onChange(); };
  const setDms = async (t, v) => {
    if (v.trim() === (t.dms_id || "")) return;
    try { await api.patch(`${base}/technicians/${t.id}`, { dms_id: v }); toast.success(`DMS ID saved for ${t.name}`); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save DMS ID"); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="hy-techs-button" className="gap-2 border-white/30 bg-white/10 text-white hover:bg-white/20">
        <Users className="h-4 w-4" /> Technicians ({shown.length})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="hy-techs-dialog">
          <DialogHeader>
            <DialogTitle>Technicians{storeName ? ` - ${storeName}` : ""}</DialogTitle>
            <DialogDescription>The AI matches the tech number printed on the RO (DMS ID) and picks the tech's {hy ? "Hyundai tech # in WebDCS" : "GMIN in GWM"}.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            <Input placeholder="Name" value={f.name} onChange={set("name")} className="min-w-[140px] flex-1" data-testid="hy-tech-name" />
            <Input placeholder="DMS ID" value={f.dms_id} onChange={set("dms_id")} className="w-24" data-testid="hy-tech-dms" />
            <Input placeholder={numLabel} value={f.number} onChange={set("number")} className="w-28" data-testid="hy-tech-number" />
            <Button onClick={add} disabled={!f.name.trim() || !store} data-testid="hy-tech-add"><Plus className="h-4 w-4" /></Button>
          </div>
          <PasteList base={base} store={store} onDone={onChange} numLabel={numLabel} />
          {!hy && <ImportList store={store} onDone={onChange} path="/gm/technicians" toBody={(r) => ({ name: r.name, number: r.gmin })} />}
          <ul className="mt-2 divide-y divide-border rounded-md border border-border">
            {shown.length === 0 && <li className="p-3 text-sm text-muted-foreground">No technicians yet.</li>}
            {shown.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 p-2.5 text-sm" data-testid={`hy-tech-${t.id}`}>
                <span>{t.name} <input defaultValue={t.dms_id || ""} placeholder="DMS ID" title="Tech number printed on the RO" onBlur={(e) => setDms(t, e.target.value)}
                  className="mx-1 w-16 rounded border border-input bg-background px-1 text-xs" data-testid={`hy-tech-dms-edit-${t.id}`} /> <span className="font-mono text-muted-foreground">{t.number ? `${numLabel} ${t.number}` : ""}</span>
                  {!t.dealership_id && <span className="ml-1 text-xs text-amber-400">not in a store</span>}</span>
                <span className="flex shrink-0 items-center">
                  {!t.dealership_id && store && <Button variant="outline" size="sm" onClick={() => moveHere(t.id)} data-testid={`hy-tech-move-${t.id}`}>Move to this store</Button>}
                  <Button variant="ghost" size="icon" onClick={() => remove(t.id)} data-testid={`hy-tech-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                </span>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function useTechs(base = "/hyundai") {
  const [techs, setTechs] = useState([]);
  const load = () => api.get(`${base}/technicians`).then(({ data }) => setTechs(data)).catch(() => {});
  useEffect(() => { load(); }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  return [techs, load];
}
